/* eslint-disable @typescript-eslint/no-unsafe-assignment --
   The reactified ymaps3 components (YMap, YMapMarker, YMapFeature, …) and the
   imperative map instance are dynamically typed at the CDN boundary. We narrow
   what we use and contain the unsafe access here, exactly as cpanel does. */
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon, MinusIcon, LocateFixedIcon } from 'lucide-react';
import type { YmapsBindings } from '@noor/maps';
import {
  fetchRoutePolyline,
  getLocationFreshness,
  freshnessColor,
  minutesAgo,
} from '@/entities/order';
import { MAP_CUSTOMIZATION } from '../lib/map-customization';
import { CourierMarker, type ExtraMarker } from './courier-marker';

interface MapCanvasProps {
  bindings: YmapsBindings;
  extraMarkers?: ExtraMarker[];
  points: { coords: [number, number]; type: string; order?: number }[];
  courierCoords?: [number, number] | undefined;
  courierLastReportedAt: string | null;
  className?: string | undefined;
}

/** Tashkent center fallback `[long, lat]`. */
const FALLBACK_CENTER: [number, number] = [69.2797, 41.3112];
const ROUTE_COLOR = '#2563EB';
const DEADHEAD_COLOR = '#7C3AED';
const BOUNDS_PADDING = 0.25;

/** Bounds `[[minLng,minLat],[maxLng,maxLat]]` from points + padding, or null. */
function computeBounds(
  coords: [number, number][],
): [[number, number], [number, number]] | null {
  if (coords.length === 0) return null;
  const lngs = coords.map((c) => c[0]);
  const lats = coords.map((c) => c[1]);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const padLng = (maxLng - minLng) * BOUNDS_PADDING || 0.01;
  const padLat = (maxLat - minLat) * BOUNDS_PADDING || 0.01;
  return [
    [minLng - padLng, minLat - padLat],
    [maxLng + padLng, maxLat + padLat],
  ];
}

export function MapCanvas({
  bindings,
  points,
  extraMarkers = [],
  courierCoords,
  courierLastReportedAt,
  className,
}: MapCanvasProps) {
  const { t } = useTranslation('orders');
  const { ymaps3, reactify } = bindings;
  const mapRef = useRef<{ update: (opts: unknown) => void; zoom?: number } | null>(null);

  // Reactified map components — memoized on the reactify binding.
  const components = useMemo(() => reactify.module(ymaps3), [reactify, ymaps3]);
  const {
    YMap,
    YMapDefaultSchemeLayer,
    YMapDefaultFeaturesLayer,
    YMapMarker,
    YMapFeature,
  } = components;

  // Route polyline from the self-hosted ORS (origin → destination); falls back
  // to a straight line between the two endpoints when ORS is unreachable, so a
  // route path is ALWAYS drawn (cpanel only had ORS and went blank on failure).
  const legs = useMemo(() => {
    const ordered = [...points].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const origins = ordered.filter((point) => point.type === 'origin');
    const destinations = ordered.filter((point) => point.type === 'destination');
    const pairs = origins
      .map((from, index) => [from.coords, destinations[index]?.coords] as const)
      .filter((pair): pair is readonly [[number, number], [number, number]] =>
        Boolean(pair[1]),
      );
    if (!pairs.length && origins[0] && destinations[0])
      pairs.push([origins[0].coords, destinations[0].coords]);
    return pairs;
  }, [points]);
  const origin = legs[0]?.[0];
  const routeKey = legs.map((pair) => pair.map((c) => c.join(',')).join('>')).join('|');
  const { data: routes = [] } = useQuery({
    queryKey: ['order-route-legs', routeKey],
    queryFn: async () =>
      Promise.all(
        legs.map(async ([from, to]) => {
          const coords: [number, number][] = [from, to];
          const snapped = await fetchRoutePolyline(coords).catch(() => null);
          return snapped && snapped.length >= 2
            ? { coords: snapped, snapped: true }
            : { coords, snapped: false };
        }),
      ),
    enabled: legs.length > 0,
    staleTime: 5 * 60_000,
  });

  const allCoords = useMemo<[number, number][]>(() => {
    const cs = points.map((p) => p.coords);
    if (courierCoords) cs.push(courierCoords);
    cs.push(...extraMarkers.map((marker) => marker.coords));
    return cs;
  }, [points, courierCoords, extraMarkers]);

  const bounds = useMemo(() => computeBounds(allCoords), [allCoords]);
  const center = points[0]?.coords ?? courierCoords ?? FALLBACK_CENTER;

  // Frame the route natively: ymaps3 accepts `location={{ bounds }}` and fits
  // the viewport to it (with the YMap `margin`), which is far more reliable than
  // an imperative post-load `update` that can race the container measuring.
  const location = useMemo(
    () => (bounds ? { bounds } : { center, zoom: 13 }),
    [bounds, center],
  );

  // Re-fit imperatively (used by the "fit to route" button + on re-mount).
  const fitBounds = useCallback(
    (duration: number) => {
      if (!bounds || !mapRef.current) return;
      mapRef.current.update({ location: { bounds, duration } });
    },
    [bounds],
  );

  const handleRef = useCallback(
    (instance: { update: (opts: unknown) => void; zoom?: number } | null) => {
      mapRef.current = instance;
    },
    [],
  );

  const zoomBy = useCallback((delta: number) => {
    const map = mapRef.current;
    if (!map) return;
    const current = typeof map.zoom === 'number' ? map.zoom : 13;
    map.update({ location: { zoom: current + delta, duration: 300 } });
  }, []);

  const courierColor = freshnessColor(getLocationFreshness(courierLastReportedAt));
  const courierPingMinutes = minutesAgo(courierLastReportedAt);
  const courierHint =
    courierPingMinutes === null
      ? undefined
      : t('detail.pingAgo', { n: courierPingMinutes });

  if (!YMap) return null;

  return (
    <div className={`relative overflow-hidden ${className ?? ''}`}>
      <YMap
        ref={handleRef}
        location={reactify.useDefault(location, [
          JSON.stringify(points.map((point) => point.coords)),
          Boolean(courierCoords),
          extraMarkers.map((marker) => marker.kind).join(','),
        ])}
        mode="vector"
        margin={[40, 40, 40, 40]}
        style={{ width: '100%', height: '100%' }}
      >
        <YMapDefaultSchemeLayer customization={MAP_CUSTOMIZATION} />
        <YMapDefaultFeaturesLayer />

        {routes.map((route, index) =>
          route.coords.length >= 2 && YMapFeature ? (
            <YMapFeature
              key={`order-leg-${index}`}
              geometry={{ type: 'LineString', coordinates: route.coords }}
              style={{
                stroke: route.snapped
                  ? [{ width: 5, color: ROUTE_COLOR }]
                  : [{ width: 4, color: ROUTE_COLOR, dash: [6, 8] }],
                opacity: route.snapped ? 1 : 0.55,
              }}
            />
          ) : null,
        )}

        {courierCoords && origin && YMapFeature && (
          <YMapFeature
            geometry={{ type: 'LineString', coordinates: [courierCoords, origin] }}
            style={{ stroke: [{ width: 4, color: DEADHEAD_COLOR, dash: [7, 7] }] }}
          />
        )}

        {points.map((p, i) => (
          <YMapMarker key={`pt-${i}`} coordinates={p.coords}>
            <PinMarker label={String(i + 1)} kind={p.type} />
          </YMapMarker>
        ))}

        {courierCoords && (
          <YMapMarker coordinates={courierCoords}>
            <span className="block -translate-x-1/2 -translate-y-1/2">
              <CourierMarker color={courierColor} hint={courierHint} />
            </span>
          </YMapMarker>
        )}
        {extraMarkers.map((marker) => {
          const markerIcon = (
            <CourierMarker
              battery={marker.kind === 'battery'}
              color={
                marker.kind === 'offer' ? '#7C3AED' : batteryColor(marker.batteryPct)
              }
              hint={
                marker.kind === 'offer'
                  ? `COURIER_ID: ${marker.courierId}`
                  : marker.batteryPct == null
                    ? t('detail.batteryLocation')
                    : `${t('detail.batteryLocation')} · ${Math.round(marker.batteryPct)}%`
              }
            />
          );
          return (
            <YMapMarker key={marker.kind} coordinates={marker.coords}>
              {marker.kind === 'offer' ? (
                <a
                  className="block -translate-x-1/2 -translate-y-1/2"
                  href={`/couriers/${marker.courierId}`}
                >
                  {markerIcon}
                </a>
              ) : (
                <span className="block translate-x-2 -translate-y-1/2">{markerIcon}</span>
              )}
            </YMapMarker>
          );
        })}
        {extraMarkers
          .filter((marker) => marker.kind === 'offer')
          .map((marker) =>
            points[0] && YMapFeature ? (
              <YMapFeature
                key="offer-line"
                geometry={{
                  type: 'LineString',
                  coordinates: [marker.coords, points[0].coords],
                }}
                style={{ stroke: [{ width: 3, color: DEADHEAD_COLOR, dash: [7, 7] }] }}
              />
            ) : null,
          )}
      </YMap>

      <div className="absolute right-3 bottom-3 z-10 flex flex-col gap-1.5">
        <MapButton onClick={() => fitBounds(400)} label={t('detail.fitToRoute')}>
          <LocateFixedIcon className="size-4" />
        </MapButton>
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <MapButton onClick={() => zoomBy(1)} label={t('map.zoomIn')} bare>
            <PlusIcon className="size-4" />
          </MapButton>
          <div className="h-px bg-border" />
          <MapButton onClick={() => zoomBy(-1)} label={t('map.zoomOut')} bare>
            <MinusIcon className="size-4" />
          </MapButton>
        </div>
      </div>
    </div>
  );
}

export function MapButton({
  onClick,
  label,
  children,
  bare = false,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
  bare?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={
        bare
          ? 'flex size-9 items-center justify-center text-foreground transition-colors hover:bg-accent'
          : 'flex size-9 items-center justify-center rounded-lg border border-border bg-card text-foreground shadow-sm transition-colors hover:bg-accent'
      }
    >
      {children}
    </button>
  );
}

function PinMarker({ label, kind }: { label: string; kind: string }) {
  const isOrigin = kind === 'origin';
  const color = isOrigin ? '#ff7900' : '#23265b';
  return (
    <span className="relative flex h-[58px] w-9 -translate-x-1/2 -translate-y-full justify-center">
      <span
        className="absolute top-0 z-10 grid size-[34px] place-items-center rounded-full border-[3px] border-white text-white shadow-md"
        style={{ backgroundColor: color }}
      >
        {isOrigin ? (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-label={label}
          >
            <path d="M3 10V7l2-4h14l2 4v3M3 10c0 3 4 3 4 0 0 3 5 3 5 0 0 3 5 3 5 0 0 3 4 3 4 0M5 13v8h14v-8M9 21v-6h6v6" />
          </svg>
        ) : (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-label={label}
          >
            <path d="M5 21V3h14l-4 5 4 5H5" />
          </svg>
        )}
      </span>
      <span
        className="absolute top-[34px] h-5 w-0.5 border-l border-white"
        style={{ backgroundColor: color }}
      />
      <span
        className="absolute bottom-0 h-[5px] w-2 rounded-[50%] border bg-white"
        style={{ borderColor: color }}
      />
    </span>
  );
}

function batteryColor(pct: number | null | undefined) {
  if (pct == null || !Number.isFinite(pct)) return '#64748B';
  if (pct <= 15) return '#DC2626';
  if (pct <= 35) return '#D97706';
  return '#16A34A';
}
